import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

export interface MaintenanceConfig {
  enabled: boolean;
  message?: string;
  estimatedDuration?: string;
  lastUpdated: Date;
  updatedBy?: string;
}

/**
 * Get maintenance mode configuration
 */
export const getMaintenanceConfig = async (): Promise<MaintenanceConfig> => {
  try {
    const maintenanceRef = doc(db, 'config', 'maintenance');
    const docSnap = await getDoc(maintenanceRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        enabled: data.enabled || false,
        message: data.message || 'We are currently performing scheduled maintenance. Please check back soon.',
        estimatedDuration: data.estimatedDuration || 'a few minutes',
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        updatedBy: data.updatedBy
      };
    } else {
      // Return default config if document doesn't exist
      const defaultConfig: MaintenanceConfig = {
        enabled: false,
        message: 'We are currently performing scheduled maintenance. Please check back soon.',
        estimatedDuration: 'a few minutes',
        lastUpdated: new Date()
      };
      
      // Create the document with default values
      await setMaintenanceConfig(defaultConfig);
      return defaultConfig;
    }
  } catch (error) {
    console.error('Error getting maintenance config:', error);
    throw error;
  }
};

/**
 * Update maintenance mode configuration
 */
export const setMaintenanceConfig = async (config: Partial<MaintenanceConfig>): Promise<void> => {
  try {
    const maintenanceRef = doc(db, 'config', 'maintenance');
    const updateData = {
      ...config,
      lastUpdated: new Date()
    };
    
    await setDoc(maintenanceRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error setting maintenance config:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time maintenance config updates
 */
export const subscribeToMaintenanceConfig = (
  callback: (config: MaintenanceConfig) => void
): (() => void) => {
  const maintenanceRef = doc(db, 'config', 'maintenance');
  
  return onSnapshot(maintenanceRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      const config: MaintenanceConfig = {
        enabled: data.enabled || false,
        message: data.message || 'We are currently performing scheduled maintenance. Please check back soon.',
        estimatedDuration: data.estimatedDuration || 'a few minutes',
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        updatedBy: data.updatedBy
      };
      callback(config);
    } else {
      // Create default config if document doesn't exist
      const defaultConfig: MaintenanceConfig = {
        enabled: false,
        message: 'We are currently performing scheduled maintenance. Please check back soon.',
        estimatedDuration: 'a few minutes',
        lastUpdated: new Date()
      };
      callback(defaultConfig);
    }
  }, (error) => {
    console.error('Error in maintenance config subscription:', error);
  });
}; 